import { HTMLAttributes, useCallback, useMemo, useRef } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import { find, indexOf, map, orderBy } from 'lodash'
import { CheckIcon } from 'lucide-react'
import Link from 'next/link'
import { useShallow } from 'zustand/shallow'

import Breadcrumbs from '@/components/base/breadcrumbs'
import Button from '@/components/base/button'
import ColorDownload from '@/components/base/color-download'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import HeartIcon from '@/components/base/heart-icon'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/base/tooltip'
import Typography from '@/components/base/typography'
import { useSchema } from '@/contexts/schema'
import { useAppStore } from '@/contexts/store'
import { useCopyToClipboard, useSSRWishlist } from '@/hooks'
import { hasFinishing, isInWishlist, useWishlistStore } from '@/store/wishlist'

import ButtonMask from '../base/button-mask'
import { cn } from '@/lib/utils'
import { Color, Finishing } from '@/types'

export type ColorHeroProps = Pick<HTMLAttributes<HTMLDivElement>, 'id'> & Color

const ColorHero = ({ id, ...color }: ColorHeroProps) => {
  const { schema } = useSchema()
  const { strings } = useAppStore()
  const wishlist = useSSRWishlist()
  const [
    addToWishlist,
    removeFromWishlist,
    addFinishingToColor,
    removeFinishingFromColor,
    enableFromWishlist,
    disableFromWishlist
  ] = useWishlistStore(
    useShallow((state) => [
      state.addToWishlist,
      state.removeFromWishlist,
      state.addFinishingToColor,
      state.removeFinishingFromColor,
      state.enableFromWishlist,
      state.disableFromWishlist
    ])
  )

  const { copied, copyToClipboard } = useCopyToClipboard()
  const currentColor = useMemo(() => find(wishlist, { id }), [id, wishlist])
  const isFinishingEmpty = useCallback(() => !currentColor?.finishing, [currentColor])
  const isFinishingActive = useCallback(
    (finishing: Finishing) => find(wishlist, { id, finishing }) !== undefined,
    [id, wishlist]
  )
  const isProductActive = useCallback(
    (id: number) => indexOf(currentColor?.finishing?.associated_products, id) > -1,
    [currentColor]
  )
  const finishingThumbNodeRef = useRef<HTMLDivElement>(null)
  const finishingCoverNodeRef = useRef<HTMLDivElement>(null)

  return (
    <Section
      className={cn('text-dark dark:text-white', {
        dark: color.theme === 'dark'
      })}
    >
      <div className="fixed left-0 top-0 size-full" style={{ backgroundColor: color.hex_color }} />
      <div className="relative overflow-hidden">
        <div className="absolute -left-0 -right-0 top-0 h-full w-auto">
          <SwitchTransition mode="out-in">
            <Transition
              nodeRef={finishingCoverNodeRef}
              key={currentColor?.finishing?.slug}
              timeout={{ exit: 300, enter: 500 }}
              unmountOnExit
            >
              {(state) => (
                <div
                  ref={finishingCoverNodeRef}
                  className={cn('size-full bg-cover bg-center bg-no-repeat bg-blend-multiply', {
                    'opacity-0': state === 'entering',
                    'opacity-80 transition-opacity duration-500 ease-in-out-cubic':
                      state === 'entered',
                    'opacity-0 transition-opacity duration-300 ease-out-cubic':
                      state === 'exiting' || state === 'exited'
                  })}
                  style={{
                    backgroundColor: color.hex_color,
                    backgroundImage: currentColor?.finishing?.acf?.image_cover.url
                      ? `url(${currentColor.finishing.acf.image_cover.url})`
                      : undefined
                  }}
                  aria-label={
                    currentColor?.finishing?.acf?.image_cover.alt || currentColor?.finishing?.name
                  }
                />
              )}
            </Transition>
          </SwitchTransition>
        </div>
        <Container
          className="relative pt-[17.2rem] md:pt-[22rem]"
          // style={{ backgroundImage: `url(${textures[0].image.url})` }}
        >
          <Grid
            className={cn(
              'col-span-2 md:col-span-6 lg:col-span-12',
              'gap-y-[2rem] md:gap-y-[4rem]'
            )}
          >
            <div className={cn('col-span-2 md:col-span-6 lg:col-span-12')}>
              {schema ? <Breadcrumbs schema={schema} /> : null}
            </div>

            <div
              className={cn(
                'col-span-2 md:col-span-6 lg:col-span-12',
                'flex items-baseline justify-between'
              )}
            >
              {color.title ? (
                <Typography variant="big-100" component="h1">
                  {color.title}
                </Typography>
              ) : null}

              <SwitchTransition mode="out-in">
                <Transition
                  nodeRef={finishingThumbNodeRef}
                  key={currentColor?.finishing?.slug}
                  timeout={300}
                  unmountOnExit
                >
                  {(state) => (
                    <div ref={finishingThumbNodeRef} className="overflow-hidden">
                      <Typography
                        variant="breadcrumbs"
                        className={cn('flex font-bold', {
                          'translate-y-full': state === 'entering',
                          'translate-y-0 transition-transform duration-300 ease-out-cubic':
                            state === 'entered',
                          '-translate-y-full transition-transform duration-300 ease-out-cubic':
                            state === 'exiting' || state === 'exited'
                        })}
                      >
                        {currentColor?.finishing?.name}
                      </Typography>
                    </div>
                  )}
                </Transition>
              </SwitchTransition>
            </div>

            <hr
              className={cn('absolute bottom-0 left-margin right-margin border-current opacity-30')}
            />
          </Grid>
        </Container>
      </div>

      <Container className="relative pt-[2rem]">
        <Grid
          className={cn(
            'col-span-2 md:col-span-6 lg:col-span-12',
            'items-start gap-y-[6rem] md:gap-y-[17rem]'
          )}
        >
          {color.solar_reflectance_index || color.high_end_color || color.recommended_fund ? (
            <Grid className={cn('col-span-2 md:col-span-6 lg:col-span-12', '')}>
              <div
                className={cn(
                  'col-span-2 md:col-span-6 lg:col-span-10 xl:col-span-8',
                  'grid grid-cols-2 gap-x-gutter gap-y-[2rem]'
                )}
              >
                {color.solar_reflectance_index ? (
                  <>
                    <Typography variant="standard" className="capitalize">
                      {strings?.labels['solar_reflectance_index']}
                    </Typography>
                    <Typography variant="standard" className="font-bold capitalize">
                      {color.solar_reflectance_index}
                    </Typography>
                  </>
                ) : null}
                {color.high_end_color ? (
                  <>
                    <Typography variant="standard" className="capitalize">
                      {strings?.labels['high_end_color']}
                    </Typography>
                    <Typography variant="standard" className="font-bold capitalize">
                      {color.high_end_color}
                    </Typography>
                  </>
                ) : null}
                {color.recommended_fund ? (
                  <>
                    <Typography variant="standard" className="capitalize">
                      {strings?.labels['recommended_fund']}
                    </Typography>
                    <Typography variant="standard" className="font-bold capitalize">
                      {color.recommended_fund}
                    </Typography>
                  </>
                ) : null}
              </div>
            </Grid>
          ) : null}

          <div
            className={cn(
              'col-span-2 md:order-2 md:col-span-3 lg:col-span-4 lg:col-start-9',
              'grid items-start gap-y-[2rem]'
            )}
          >
            <div className="grid gap-y-[4rem]">
              {color.products?.finishings?.length > 0 &&
              strings?.labels?.featured_finishes_title ? (
                <Typography variant="standard" paragraph>
                  {strings.labels.featured_finishes_title}
                </Typography>
              ) : null}

              {color.products?.finishings?.length > 0 ? (
                <ul className="grid grid-cols-4 gap-[1.8rem] xl:grid-cols-5">
                  <li className={cn('col-span-1')}>
                    <button
                      onClick={() => {
                        isInWishlist(id, wishlist)
                          ? removeFinishingFromColor(id)
                          : removeFromWishlist(id)
                      }}
                      className={cn(
                        'group/thumb relative flex aspect-square size-[6.4rem] overflow-hidden rounded-[0.5rem]',
                        'before:absolute before:left-0 before:top-0 before:z-10 before:size-full before:opacity-50',
                        'before:rounded-[0.5rem] before:border before:border-dashed before:border-current before:opacity-50',
                        'before:transition-all before:duration-300 before:ease-in-out-cubic',
                        'after:absolute after:left-0 after:top-0 after:z-10 after:size-full',
                        'after:rounded-[0.5rem] after:border-[0rem] after:border-solid after:border-current',
                        'after:transition-all after:duration-150 after:ease-in-out-cubic',
                        'hover:before:opacity-100',
                        {
                          'pointer-events-none opacity-100 after:border-[0.5rem]':
                            isFinishingEmpty()
                        },
                        color.theme === 'dark' ? 'focus-ring-light' : 'focus-ring-dark'
                      )}
                    >
                      <span
                        className={cn(
                          'absolute left-1/2 top-1/2 h-[0.2rem] w-[6rem] bg-current opacity-50',
                          '-translate-x-1/2 -translate-y-1/2 -rotate-45',
                          'transition-all duration-150 ease-in-out-cubic',
                          'group-hover/thumb:opacity-100'
                        )}
                      />
                    </button>
                  </li>
                  {map(
                    orderBy(color.products.finishings, ['name'], ['asc']),
                    (finishing, index) => (
                      <li key={`finishing-${index}`} className={cn('col-span-1')}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                isInWishlist(id, wishlist, true)
                                  ? addFinishingToColor(id, finishing)
                                  : addToWishlist({ ...color, id, finishing, enabled: false })
                              }}
                              className={cn(
                                'group/thumb relative isolate flex aspect-square size-[6.4rem] overflow-hidden rounded-[0.5rem]',
                                'before:absolute before:left-0 before:top-0 before:z-10 before:size-full before:opacity-50',
                                'before:rounded-[0.5rem] before:border before:border-dashed before:border-current before:opacity-50',
                                'before:transition-all before:duration-300 before:ease-in-out-cubic',
                                'after:absolute after:left-0 after:top-0 after:z-10 after:size-full',
                                'after:rounded-[0.5rem] after:border-[0rem] after:border-solid after:border-current',
                                'after:transition-all after:duration-150 after:ease-in-out-cubic',
                                'hover:before:opacity-100',
                                {
                                  'opacity-100 after:border-[0.5rem]': isFinishingActive(finishing)
                                },
                                color.theme === 'dark' ? 'focus-ring-light' : 'focus-ring-dark'
                              )}
                              style={{ backgroundColor: color.hex_color }}
                            >
                              {finishing ? (
                                <span
                                  className={cn(
                                    'relative z-0 block size-full bg-cover bg-center bg-no-repeat bg-blend-multiply',
                                    'opacity-50 transition-opacity duration-300 ease-in-out-cubic',
                                    'group-hover/thumb:opacity-100',
                                    {
                                      'opacity-100': isFinishingActive(finishing)
                                    }
                                  )}
                                  style={{
                                    backgroundColor: color.hex_color,
                                    backgroundImage: `url(${finishing.acf?.image_thumb.url})`
                                  }}
                                  aria-label={finishing.acf?.image_thumb.alt || finishing.name}
                                />
                              ) : null}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <Typography variant="breadcrumbs" paragraph>
                              {finishing.name}
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </li>
                    )
                  )}
                </ul>
              ) : null}

              <div className="mr-0 mt-auto grid gap-y-[1rem]">
                <div className="flex items-center justify-end space-x-[1rem]">
                  <Button
                    variant="secondary"
                    className={cn(
                      'min-w-none w-[5rem] shrink-0 grow-0',
                      'hover:before:bg-transparent dark:hover:before:bg-transparent'
                    )}
                    endIcon={<HeartIcon active={isInWishlist(id, wishlist)} />}
                    onClick={() =>
                      isInWishlist(id, wishlist, true)
                        ? isInWishlist(id, wishlist)
                          ? hasFinishing(id, wishlist)
                            ? disableFromWishlist(id)
                            : removeFromWishlist(id)
                          : enableFromWishlist(id)
                        : addToWishlist({ ...color, id, finishing: null, enabled: true })
                    }
                  />

                  <Button
                    variant="secondary"
                    className={cn(
                      'min-w-none grow',
                      'hover:before:bg-transparent dark:hover:before:bg-transparent'
                    )}
                    endIcon={<SVGIcon symbol="live-room" className="size-[2rem]" />}
                    asChild
                  >
                    <Link href="/liveroom">
                      <ButtonMask>
                        <Typography variant="button">{strings?.labels?.liveroom}</Typography>
                      </ButtonMask>
                    </Link>
                  </Button>
                </div>

                <div className="flex items-center justify-end space-x-[1rem]">
                  <Button
                    variant="secondary"
                    className={cn(
                      'min-w-none grow',
                      'hover:before:bg-transparent dark:hover:before:bg-transparent'
                    )}
                    onClick={() => copyToClipboard(color.hex_color)}
                    endIcon={
                      copied ? (
                        <CheckIcon size={16} />
                      ) : (
                        <SVGIcon symbol="copy" className="size-[1.6rem]" />
                      )
                    }
                  >
                    <ButtonMask>
                      <Typography variant="button">{`${strings?.labels?.srgb}, ${color.hex_color.substring(1)}`}</Typography>
                    </ButtonMask>
                  </Button>

                  <ColorDownload
                    id={id}
                    {...color}
                    finishing={currentColor?.finishing || null}
                    className={cn('min-w-none grow')}
                    debug={false}
                    /* debug={
                      process.env.NODE_ENV === 'development' &&
                      process.env.NEXT_PUBLIC_COLOR_DOWNLOAD_DEBUG === 'true'
                    } */
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            className={cn(
              'col-span-2 md:order-1 md:col-span-3 lg:col-span-6',
              'grid grid-cols-2 items-start gap-x-gutter gap-y-[6rem]'
            )}
          >
            <div className={cn('col-span-2', 'grid gap-y-[0.5rem]')}>
              {strings?.labels?.featured_products_title ? (
                <Typography variant="standard" paragraph className="opacity-70">
                  {strings.labels.featured_products_title}
                </Typography>
              ) : null}

              {strings?.labels?.featured_products_text ? (
                <Typography variant="breadcrumbs" paragraph>
                  {strings.labels.featured_products_text}
                </Typography>
              ) : null}
            </div>

            {map(color.products.items, ({ category, products }, index) => (
              <div
                key={`category-${index}`}
                className={cn('col-span-2 lg:col-span-1', 'grid gap-y-[2rem]')}
              >
                <Typography variant="medium-upper" className="font-medium uppercase underline">
                  {category.name}
                </Typography>
                <ul className="grid gap-y-[2rem]">
                  {map(orderBy(products, ['title'], ['asc']), ({ id, title, link }, index) => (
                    <li key={`product-${index}`}>
                      <Link
                        href={link}
                        scroll={false}
                        className={cn(
                          'group/product relative inline-flex items-center',
                          '-m-[0.5rem] p-[0.5rem] opacity-60',
                          'transition-opacity duration-300 ease-out-cubic',
                          'hover:opacity-100 focus-visible:outline-none',
                          {
                            'opacity-100': isProductActive(id)
                          }
                        )}
                      >
                        <span className="absolute -left-[1.8rem] top-1/2 -translate-y-1/2">
                          <SVGIcon
                            symbol="arrow-right"
                            className={cn(
                              'size-[1.5rem] -translate-x-full opacity-0',
                              'transition-all duration-300 ease-out-cubic',
                              {
                                'translate-x-0 opacity-100': isProductActive(id)
                              }
                            )}
                          />
                        </span>

                        <Typography
                          variant="medium-upper"
                          className={cn(
                            'font-medium uppercase',
                            '-m-2 rounded-md p-2',
                            'group-focus-visible/product:ring-2',
                            {
                              'group-focus-visible/product:ring-[var(--focus-ring-light)]':
                                color.theme === 'dark',
                              'group-focus-visible/product:ring-[var(--focus-ring-dark)]':
                                color.theme === 'light'
                            }
                          )}
                        >
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default ColorHero
